import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  ShoppingCart,
  ProjectorScreenChart,
  Stack,
  StackPlus,
  Truck,
  DownloadSimple,
  ListBullets,
  Waves,
  Cube,
  CalendarX,
  Package,
  Shuffle,
  BellSimple,
} from '@phosphor-icons/react';
import { useAuth } from './auth';

interface ISubMenu {
  phosphorIcon: ReactNode;
  key: string;
  text: string;
  link: string;
  genericLink?: string;
  /** Notification count from sidebar metadata (e.g. inventory badges). */
  count?: number;
}

interface IMenu {
  text: string;
  link?: string;
  parentLink: string;
  key: string;
  phosphorIcon: ReactNode;
  menus: ISubMenu[];
  count?: number;
}

interface ISidebarContext {
  menus: IMenu[];
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  toggleSidebarVisibility: (showSidebar?: boolean) => void;
  showSidebar: boolean;
  setFooterActive: (active: boolean) => void;
  footerActive: boolean;
}

interface ISidebarProvider {
  children: ReactNode;
}

const SidebarContext = createContext<ISidebarContext | undefined>(undefined);

export function SidebarProvider({ children }: ISidebarProvider) {
  const { user } = useAuth();
  // in some cases, we may need to hide the sidebar
  const [showSidebar, setShowSidebar] = useState<boolean>(true);
  const [footerActive, setFooterActive] = useState<boolean>(false);

  const toggleSidebarVisibility = (showSidebar: boolean = true) => {
    setShowSidebar(showSidebar);
  };

  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);

  const menus = useMemo<IMenu[]>(() => {
    const temp: IMenu[] = [];
    temp.push(
      {
        text: 'Inventory',
        link: '/app/inventory',
        parentLink: '/app/inventory',
        key: 'inventory-parent',

        phosphorIcon: <Stack className="sidebar-icon-main" />,
        menus: [
          {
            phosphorIcon: <StackPlus className="sidebar-icon" />,
            text: 'Inventory Import',
            link: '/app/inventory/import',
            genericLink: '/app/inventory/import',
            key: 'inventory-import',
          },
          {
            phosphorIcon: <ListBullets className="sidebar-icon" />,
            key: 'inventory-list',
            text: 'Inventory List',
            link: '/app/inventory/list',
          },
          {
            phosphorIcon: <CalendarX className="sidebar-icon" />,
            key: 'inventory-expiration',
            text: 'Inventory Expirations',
            link: '/app/inventory/expirations',
          },
          {
            phosphorIcon: <Shuffle className="sidebar-icon" />,
            key: 'inventory-transfer',
            text: 'Inventory Transfer',
            link: '/app/inventory/transfer',
            genericLink: '/app/inventory/transfer',
          },
          {
            phosphorIcon: <BellSimple className="sidebar-icon" />,
            key: 'inventory-replenishment',
            text: 'Stock Alerts',
            link: '/app/inventory/replenishment',
          },
        ],
      },
      {
        text: 'Shipments',
        link: '/app/shipments/inbound',
        parentLink: '/app/shipments/inbound',
        key: 'shipment-parent',

        phosphorIcon: <Truck className="sidebar-icon-main" />,
        menus: [
          {
            phosphorIcon: <DownloadSimple className="sidebar-icon" />,
            text: 'Inbound',
            key: 'shipment-inbound',
            link: '/app/shipments/inbound',
            genericLink: '/app/shipments/inbound',
          },
        ],
      },
      {
        text: 'Fulfill',
        parentLink: '/app/fulfill',
        key: 'order-parent',
        phosphorIcon: <ShoppingCart className="sidebar-icon-main" />,
        menus: [
          {
            phosphorIcon: <Cube className="sidebar-icon" />,
            key: 'fulfill-orders',
            text: 'Orders',
            link: '/app/fulfill/orders/',
            genericLink: '/app/fulfill/orders',
          },
          {
            phosphorIcon: <Waves className="sidebar-icon" />,
            text: 'Waves',
            key: 'wave-list',
            link: '/app/fulfill/waves/list/building',
            genericLink: '/app/fulfill/waves',
          },
          {
            phosphorIcon: <Package className="sidebar-icon" />,
            text: 'Packing',
            key: 'packing',
            link: '/app/fulfill/packing/list/to-pack',
            genericLink: '/app/fulfill/packing',
          },
        ],
      },
    );

    if (user?.isSuperAdmin) {
      temp.push({
        text: 'Reports',
        link: '/app/reports/manifest',
        parentLink: '/app/reports',
        key: 'reports-parent',
        phosphorIcon: <ProjectorScreenChart className="sidebar-icon-main" />,
        menus: [
          {
            phosphorIcon: <ListBullets className="sidebar-icon" />,
            text: 'Manifest Report',
            key: 'manifest-report',
            link: '/app/reports/manifest',
            genericLink: '/app/reports/manifest',
          },
        ],
      });
    }

    return temp;
  }, [user?.isSuperAdmin]);

  return (
    <SidebarContext.Provider
      value={{
        menus,
        isCollapsed,
        setIsCollapsed,
        toggleSidebarVisibility,
        showSidebar,
        setFooterActive,
        footerActive,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

export const useSidebar = (): ISidebarContext => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
